import React, { memo } from 'react';
import { Card, Statistic, Tooltip, Spin } from 'antd';
import { 
  ArrowUpOutlined, 
  ArrowDownOutlined, 
  MinusOutlined,
  LoadingOutlined 
} from '@ant-design/icons';
import { getStatusColor, getStatusIcon, getStatusText } from '../../config/performanceDashboard';
import './PerformanceDashboard.css';

const MetricCard = memo(({
  title,
  value,
  unit = '',
  status = 'unknown',
  trend = null,
  loading = false,
  icon = null,
  tooltip = null,
  className = '',
  style = {},
  onClick = null,
  children,
}) => {
  // Get status styling
  const statusColor = getStatusColor(status);
  const statusIcon = getStatusIcon(status);
  const statusText = getStatusText(status);

  // Format value with unit
  const formatValue = (val, unit) => {
    if (val === null || val === undefined) return 'N/A';
    
    if (typeof val === 'number') {
      if (unit === '%') {
        return `${val.toFixed(1)}${unit}`;
      } else if (unit === 'ms' || unit === 's') {
        return `${val.toFixed(2)}${unit}`;
      } else if (unit === 'MB' || unit === 'GB' || unit === 'KB') {
        return `${val.toFixed(1)}${unit}`;
      } else {
        return `${val.toLocaleString()}${unit}`;
      }
    }
    
    return `${val}${unit}`;
  };

  // Get trend icon and color
  const getTrendIcon = () => {
    if (trend === null) return null;
    
    if (trend > 0) {
      return <ArrowUpOutlined style={{ color: '#ff4d4f' }} />;
    } else if (trend < 0) {
      return <ArrowDownOutlined style={{ color: '#52c41a' }} />;
    } else {
      return <MinusOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  // Get trend text
  const getTrendText = () => {
    if (trend === null) return '';
    
    if (trend > 0) {
      return `+${Math.abs(trend).toFixed(1)}%`;
    } else if (trend < 0) {
      return `-${Math.abs(trend).toFixed(1)}%`;
    } else {
      return '0%';
    }
  };

  // Card content
  const cardContent = (
    <Card
      className={`metric-card ${className} ${onClick ? 'clickable' : ''}`}
      style={{
        ...style,
        borderLeft: `4px solid ${statusColor}`,
      }}
      onClick={onClick}
      hoverable={!!onClick}
    >
      <div className="metric-card-header">
        <div className="metric-card-title">
          {icon && <span className="metric-card-icon">{icon}</span>}
          <span className="metric-card-title-text">{title}</span>
        </div>
        <div className="metric-card-status">
          <span 
            className="metric-card-status-indicator"
            style={{ color: statusColor }}
          >
            {statusIcon}
          </span>
          <span 
            className="metric-card-status-text"
            style={{ color: statusColor }}
          >
            {statusText}
          </span>
        </div>
      </div>

      <div className="metric-card-content">
        {loading ? (
          <div className="metric-card-loading">
            <Spin 
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} 
              size="large"
            />
          </div>
        ) : (
          <Statistic
            value={formatValue(value, unit)}
            valueStyle={{
              color: statusColor,
              fontSize: '24px',
              fontWeight: 'bold',
            }}
            className="metric-card-statistic"
          />
        )}
      </div>

      {trend !== null && !loading && (
        <div className="metric-card-trend">
          <span className="metric-card-trend-icon">
            {getTrendIcon()}
          </span>
          <span className="metric-card-trend-text">
            {getTrendText()}
          </span>
        </div>
      )}

      {children && (
        <div className="metric-card-children">
          {children}
        </div>
      )}
    </Card>
  );

  // Wrap with tooltip if provided
  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement="top">
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
});

MetricCard.displayName = 'MetricCard';

export default MetricCard;
